import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Card, CardContent } from '../../../../../components/ui/card';
import { DashboardProject } from '../../../types/project';
import { PipelineSetup, PipelineSetupRef } from './components/PipelineSetup';
import { FabricationSetup, FabricationSetupRef } from './components/FabricationSetup';
import { OthersSetup, OthersSetupRef } from './components/OthersSetup';
import { Loader2, Plus, Cable, Wrench, Activity, Save, Trash2 } from 'lucide-react';
import { pipeSizes } from '../../../mocks/material';
import { Button } from '../../../../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../../../components/ui/tabs';
import { Notification as customNotification } from '../../../../../components/ui/custom-notification';
import {
  ProjectSetupData,
  PipelineSetupData,
  FabricationSetupData,
  SetupState1,
  PipeSize1,
  buildSpoolDetailsPayload,
  getNumberOfSpoolsFromPipe,
  getSpoolDetailsFromPipe,
  normalizeSpoolDetailsRows,
} from '../../../types/setup';

import { SaveProjectSetup } from '../../../../../redux/Project/setUpSaga';
import { fetchProjectList } from '../../../../../redux/Project/action';
import { getAllPipeDetails, getListManufacturingCategory } from '../../../../../redux/Project/setupData/action';
import { useDispatch, useSelector } from 'react-redux';
import { assignJobNumberToPipe } from '../../../../../redux/WeldData/actions';
import { PipeInPipeSetup } from './components/PipeInPipeSetup';
import { getUnitFromSettings, convertSetupPipeToInchesForSave, convertSetupPipeFromInchesForDisplay, convertPipefitterFieldsToMm, convertPipefitterFieldsFromMm, roundHiLoThreshold } from '../../../../../utils/unitUtils';
import { getCrossPipeDuplicateJobNumberErrors } from '../../../utils/setupJobNumberValidation';
import {
  buildPipefitterProjectPayloadFromSetupPipe,
  hiloThresholdFromPipe,
} from '../../../utils/pipefitterProjectPayload';
import pipefitterApi from '../../../../../services/pipefitterApi';
import { syncSetupPipeGroupAssignment } from '../../../../../services/syncSetupPipeGroupApi';

function numberOrUndefined(value: unknown): number | undefined {
  if (value == null || value === '') return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function isPipefitterEnabledForProject(
  projectTypes: DashboardProject['project']['projectTypes'] | undefined
): boolean {
  if (!projectTypes) return false;
  if (projectTypes.pipeline?.enabled && projectTypes.pipeline.features?.pipeFitter?.enabled) {
    return true;
  }
  const fabOpts = projectTypes.fabrication?.options;
  if (projectTypes.fabrication?.enabled && fabOpts) {
    const keys = ['buckleArrestor', 'pipeInPipe', 'bulkhead', 'spools'] as const;
    if (keys.some((key) => fabOpts[key]?.selected && fabOpts[key]?.features?.pipeFitter?.enabled)) {
      return true;
    }
  }
  return Boolean(projectTypes.others?.enabled && projectTypes.others.features?.pipeFitter?.enabled);
}

function getLastSavedPipe(pipes: unknown): Record<string, unknown> | null {
  if (!Array.isArray(pipes) || pipes.length === 0) return null;
  const last = pipes[pipes.length - 1];
  return last && typeof last === 'object' ? (last as Record<string, unknown>) : null;
}

async function syncPipefitterProjectFromSetupPipe(
  project: DashboardProject['project'],
  pipe: Record<string, unknown>
): Promise<void> {
  const payload = buildPipefitterProjectPayloadFromSetupPipe(project, pipe);
  await pipefitterApi.post('/project/create-or-update-project', payload);
}

async function syncPipefitterPipeGroupFromSetupPipe(
  project: DashboardProject['project'],
  apiPipe: Record<string, unknown>,
  previousGroupId?: string | null
): Promise<void> {
  const pipeId = numberOrUndefined(apiPipe.pipeId);
  const projectId = numberOrUndefined(project.projectId);
  const projectNumber = String(project.projectNumber ?? '').trim();
  if (pipeId == null || projectId == null || !projectNumber) return;

  const groupId =
    apiPipe.groupId != null ? String(apiPipe.groupId).trim() : '';
  const prev =
    previousGroupId != null ? String(previousGroupId).trim() : '';

  await syncSetupPipeGroupAssignment({
    projectNumber,
    projectId,
    pipeId,
    groupId: groupId || null,
    previousGroupId: prev || null,
  });
}

/** Normalize server payloads that still use legacy keys into canonical pipefitter setup fields. */
function mergeOorToleranceFromApi(pipe: Record<string, any>) {
  const hiloThreshold =
    pipe.hiloThreshold ?? pipe.hilo_threshold ?? pipe.hiloThresholdMm;
  return {
    ...pipe,
    oORIDTolerance:
      pipe.oORIDTolerance ?? pipe.ooRIDTolrence ?? pipe.oORIDTolrence,
    oORODTolerance:
      pipe.oORODTolerance ?? pipe.ooRODTolrence ?? pipe.oORODTolrence,
    ...(hiloThreshold != null && hiloThreshold !== ''
      ? { hiloThreshold: roundHiLoThreshold(Number(hiloThreshold)) }
      : {}),
  };
}

interface ProjectSetupProps {
  project: DashboardProject;
  OnChanged: (hasUnsavedChanges: boolean) => void;
  canEdit?: boolean;
}

export const ProjectSetup: React.FC<ProjectSetupProps> = ({ project, OnChanged, canEdit = true }) => {
  const dispatch = useDispatch();
  const { pipeDetails, isPipeDetailsLoading, manufacturingCategories, isManufacturingCategoryLoading } = useSelector((state: any) => state.setupData || {});
  const [isLoading, setIsLoading] = useState(false);
  const [setupTab, setSetupTab] = useState<'pipeline' | 'fabrication' | 'others'>(project.project.projectTypes.pipeline ? 'pipeline' : project.project.projectTypes.fabrication ? 'fabrication' : 'others');
  const [isLoadingData, setIsLoadingData] = useState(false)
  const [manufacturingList, setManufacturingList] = useState<any[]>([])
  const [selectedManufacturingId, setSelectedManufacturingId] = useState('')
  const [pipelineData, setPipelineData] = useState<any[]>([])
  const [unit, setUnit] = useState(project.project.unit || 'in')
  const [showValidation, setShowValidation] = useState(false);
  const [setupData, setSetupData] = useState<any>({});
  const [originalSetupData, setOriginalSetupData] = useState<any>({});
  const [editMode, setEditMode] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastProjectId, setLastProjectId] = useState<string | number | null>(null);
  /** After API reload (initial load or post-save), re-sync comparison baseline from form state. */
  const baselineSyncNeededRef = useRef(false);

  const [defaultUnit, setDefaultUnit] = useState('in');
  useEffect(() => {
    if (project.project.unit) {
      setDefaultUnit(project.project.unit || 'in');
      setUnit(project.project.unit);
    }
  }, [project.project.unit]);

  // Load initial data - only fetch if project ID changed or pipeDetails doesn't match current project
  useEffect(() => {
    if (isPipeDetailsLoading) {
      return;
    }
    const currentProjectId = project.project.projectId;

    // Check if project ID changed
    const projectIdChanged = lastProjectId !== null && lastProjectId !== currentProjectId;

    // Check if pipeDetails exists and matches current project ID
    const pipeDetailsMatchesProject = pipeDetails && Object.keys(pipeDetails).length > 0 &&
      Object.values(pipeDetails).some((setup: any) =>
        setup.projectId && String(setup.projectId) === String(currentProjectId)
      );

    // Only fetch pipeDetails if:
    // 1. Project ID changed (need to fetch new project's data)
    // 2. pipeDetails is null/undefined/empty
    // 3. pipeDetails exists but doesn't match current project ID
    if (currentProjectId) {
      if (projectIdChanged || !pipeDetails || Object.keys(pipeDetails).length === 0 || !pipeDetailsMatchesProject) {
        dispatch(getAllPipeDetails({ projectId: currentProjectId }));
      }

      // Fetch manufacturing categories if pipeline is enabled (this doesn't depend on project ID, so only fetch once)
      if (project.project.projectTypes.pipeline?.enabled && (!manufacturingCategories || manufacturingCategories.length === 0)) {
        dispatch(getListManufacturingCategory({}));
      }
    }

    // Update last project ID
    if (currentProjectId) {
      setLastProjectId(currentProjectId);
    }
  }, [project.project.projectId]);


  useEffect(() => {
    const fetchSettings = async () => {
      const unitFromSettings = getUnitFromSettings();
      setUnit(project.project.unit || unitFromSettings);
    };
    fetchSettings();
  }, []);

  // Refs to access child component data
  const pipelineSetupRef = useRef<PipelineSetupRef>(null);
  const fabricationSetupRef = useRef<FabricationSetupRef>(null);
  const othersSetupRef = useRef<OthersSetupRef>(null);

  const getHasAnyPipes = (state: SetupState1): boolean => {
    return state.pipeSizes && state.pipeSizes.length > 0
  }

  // Check if a pipe has valid data (not empty or zero values)
  const hasValidPipeData = (pipe: any): boolean => {
    return pipe.pipeName && pipe.pipeName.trim() !== '' &&
      pipe.wallThickness && pipe.wallThickness > 0 &&
      pipe.numberOfPipes && pipe.numberOfPipes > 0 &&
      pipe.pipeLength && pipe.pipeLength > 0 &&
      pipe.manufacturerIds && pipe.manufacturerIds.length > 0

  }

  // Check if a state has any valid pipes with actual data
  const hasValidPipesInState = (state: SetupState1): boolean => {
    if (!state.pipeSizes?.length) return false;
    if (state.projectSubType === 'spools') {
      return state.pipeSizes.some(
        (pipe) =>
          Boolean(pipe.pipeName && String(pipe.pipeName).trim() !== '') &&
          getNumberOfSpoolsFromPipe(pipe) > 0
      );
    }
    return state.pipeSizes.some((pipe: any) => hasValidPipeData(pipe));
  };

  const spoolRowsSignature = (pipe: PipeSize1, count: number): string =>
    JSON.stringify(normalizeSpoolDetailsRows(count, getSpoolDetailsFromPipe(pipe)));

  /** Detect edits to spool rows (name, type, length, welds, etc.) for fabrication spools setup. */
  const hasSpoolsSetupDetailChanges = (): boolean => {
    if (stateSpools.projectSubType !== 'spools' || !stateSpools.pipeSizes?.length) {
      return false;
    }
    const spoolsKey = String(
      stateSpools.projectTypeId ||
      project.project.projectTypes.fabrication?.options?.spools?.project_type_id ||
      ''
    );
    const originalSetup = spoolsKey ? originalSetupData[spoolsKey] : null;

    if (!originalSetup?.pipeSizes?.length) {
      return stateSpools.pipeSizes.some((pipe) => {
        const n = getNumberOfSpoolsFromPipe(pipe);
        if (n <= 0) return false;
        const rows = normalizeSpoolDetailsRows(n, getSpoolDetailsFromPipe(pipe as PipeSize1));
        return rows.some(
          (r) =>
            Boolean(r.spoolType?.trim()) ||
            Boolean(String(r.spoolLength ?? '').trim()) ||
            Boolean(String(r.welds ?? '').trim()) ||
            (Boolean(r.measureType?.trim()) && r.measureType !== 'N/A')
        );
      });
    }

    for (let i = 0; i < stateSpools.pipeSizes.length; i++) {
      const currentPipe = stateSpools.pipeSizes[i] as PipeSize1;
      const originalPipe = originalSetup.pipeSizes[i] as PipeSize1 | undefined;
      const nCur = getNumberOfSpoolsFromPipe(currentPipe);
      const nOrig = originalPipe ? getNumberOfSpoolsFromPipe(originalPipe) : 0;

      if (nCur !== nOrig) return true;

      if (
        spoolRowsSignature(currentPipe, nCur) !==
        spoolRowsSignature(originalPipe as PipeSize1, nOrig || nCur)
      ) {
        return true;
      }
    }
    return false;
  };

  /** Compare inch values from save payload vs API (tolerates mm↔in conversion rounding). */
  const setupInchesMatch = (a: unknown, b: unknown): boolean => {
    const na = a == null || a === '' ? 0 : Number(a);
    const nb = b == null || b === '' ? 0 : Number(b);
    if (!Number.isFinite(na) || !Number.isFinite(nb)) return na === nb;
    return Math.abs(na - nb) < 0.00005;
  };

  // Check if there are any changes compared to the original setup data
  const hasActualChanges = (): boolean => {
    if (hasSpoolsSetupDetailChanges()) {
      return true;
    }

    const currentData = getCurrentSetupData();

    // If no current data, no changes
    if (!currentData || Object.keys(currentData).length === 0) {
      return false;
    }

    // Compare with original setup data to detect actual changes
    for (const [key, currentSetup] of Object.entries(currentData)) {
      const originalSetup = originalSetupData[key];

      // If this is a new setup (not in original data), it's a change
      if (!originalSetup) {
        return true;
      }

      // Compare pipe sizes
      if (currentSetup.pipeSizes && originalSetup.pipeSizes) {
        // Check if number of pipes changed
        if (currentSetup.pipeSizes.length !== originalSetup.pipeSizes.length) {
          return true;
        }

        // Check if any pipe data changed
        for (let i = 0; i < currentSetup.pipeSizes.length; i++) {
          const currentPipe = currentSetup.pipeSizes[i];
          const originalPipe = originalSetup.pipeSizes[i];

          if (!originalPipe) {
            return true; // New pipe added
          }
          if (currentPipe?.manufacturerIds?.length !== originalPipe?.manufacturerIds?.length) {
            return true;
          }
          if (currentPipe?.manufacturerIds?.some((id: any) => !originalPipe?.manufacturerIds?.includes(id))) {
            return true;
          }

          // Helper function to compare jobNumber objects deeply
          const compareJobNumbers = (currentJobNumber: any, originalJobNumber: any): boolean => {
            // If both are null/undefined, they're equal
            if (!currentJobNumber && !originalJobNumber) {
              return true;
            }

            // If one is null/undefined and the other isn't, they're different
            if (!currentJobNumber || !originalJobNumber) {
              return false;
            }

            // Compare numberOfJobs
            if (currentJobNumber.numberOfJobs !== originalJobNumber.numberOfJobs) {
              return false;
            }

            // Compare jobNumbers array values (deep comparison)
            const currentJobNumbers = currentJobNumber.jobNumbers || [];
            const originalJobNumbers = originalJobNumber.jobNumbers || [];

            // Check if arrays have different lengths
            if (currentJobNumbers.length !== originalJobNumbers.length) {
              return false;
            }

            // Compare each job number string value
            for (let i = 0; i < currentJobNumbers.length; i++) {
              const currentValue = String(currentJobNumbers[i] || '').trim();
              const originalValue = String(originalJobNumbers[i] || '').trim();

              if (currentValue !== originalValue) {
                return false;
              }
            }

            return true;
          };

          //  Compare pipe properties
          if (currentPipe.pipeName !== originalPipe.pipeName ||
            !setupInchesMatch(currentPipe.pipeSize, originalPipe.pipeSize) ||
            !setupInchesMatch(currentPipe.wallThickness, originalPipe.wallThickness) ||
            currentPipe.numberOfPipes !== originalPipe.numberOfPipes ||
            !setupInchesMatch(currentPipe.pipeLength, originalPipe.pipeLength) ||
            !compareJobNumbers(currentPipe.jobNumber, originalPipe.jobNumber)) {
            return true;
          }

          if (currentSetup.projectType === 'fabrication' && currentSetup.projectSubType === 'buckleArrestor') {
            if (currentPipe.numberOfBuckleArrestors !== originalPipe.numberOfBuckleArrestors ||
              currentPipe.numberOfForges !== originalPipe.numberOfForges ||
              currentPipe.outputAssemblyLength !== originalPipe.outputAssemblyLength
              || currentPipe.prefixAssemblyName !== originalPipe.prefixAssemblyName
            ) {


              return true;
            }
          }

          if (currentSetup.projectType === 'fabrication' && currentSetup.projectSubType === 'bulkhead') {
            if (currentPipe.numberOfBuckleArrestors !== originalPipe.numberOfBuckleArrestors ||
              currentPipe.outputAssemblyLength !== originalPipe.outputAssemblyLength ||
              currentPipe.prefixAssemblyName !== originalPipe.prefixAssemblyName) {

              return true;
            }
          }

          if (currentSetup.projectType === 'fabrication' && currentSetup.projectSubType === 'pipeInPipe') {
            if (currentPipe.numberOfBuckleArrestors !== originalPipe.numberOfBuckleArrestors ||
              currentPipe.outputAssemblyLength !== originalPipe.outputAssemblyLength ||
              currentPipe.prefixAssemblyName !== originalPipe.prefixAssemblyName) {
              return true;
            }
          }

          if (currentSetup.projectType === 'fabrication' && currentSetup.projectSubType === 'spools') {
            if (
              getNumberOfSpoolsFromPipe(currentPipe as PipeSize1 & Record<string, unknown>) !==
              getNumberOfSpoolsFromPipe(originalPipe as PipeSize1 & Record<string, unknown>) ||
              currentPipe.numberOfForges !== originalPipe.numberOfForges ||
              currentPipe.outputAssemblyLength !== originalPipe.outputAssemblyLength ||
              currentPipe.prefixAssemblyName !== originalPipe.prefixAssemblyName
            ) {
              return true;
            }
            const curSpools = JSON.stringify(
              normalizeSpoolDetailsRows(
                getNumberOfSpoolsFromPipe(currentPipe as PipeSize1 & Record<string, unknown>),
                getSpoolDetailsFromPipe(currentPipe as PipeSize1)
              )
            );
            const origSpools = JSON.stringify(
              normalizeSpoolDetailsRows(
                getNumberOfSpoolsFromPipe(originalPipe as PipeSize1 & Record<string, unknown>),
                getSpoolDetailsFromPipe(originalPipe as PipeSize1)
              )
            );
            if (curSpools !== origSpools) {
              return true;
            }
          }

          // Compare pipeDetailsExtraDetails (isReducerPipe + isFlangePipe + pipeDescription) — same pattern for all project types
          const getExtraFlags = (pipe: any) => {
            const extra =
              pipe?.pipeDetailsExtraDetails && typeof pipe.pipeDetailsExtraDetails === 'object'
                ? (pipe.pipeDetailsExtraDetails as Record<string, unknown>)
                : {};
            return {
              isReducerPipe: !!extra.isReducerPipe,
              isFlangePipe: !!extra.isFlangePipe,
              pipeDescription:
                typeof extra.pipeDescription === 'string' ? extra.pipeDescription.trim() : '',
            };
          };
          if (JSON.stringify(getExtraFlags(currentPipe)) !== JSON.stringify(getExtraFlags(originalPipe))) {
            return true;
          }

          // Optional free-text group id (empty allowed)
          if (String(currentPipe?.groupId ?? '').trim() !== String(originalPipe?.groupId ?? '').trim()) {
            return true;
          }

          // Compare pipefitter config fields (so Save enables when these change)
          const pipefitterFields = [
            'toeSeamSeparationMinMm', 'seamCapWidthMaxMm', 'sixOclockExclusionDeg', 'hiloWpsLimitMm',
            'excessiveHiloLengthLimitDeg', 'hiloPreferredLimitTopMm', 'hiloPreferredLimitMiddleMm', 'hiloPreferredLimitLowerMm'
          ] as const;
          for (const field of pipefitterFields) {
            const cur = currentPipe?.[field];
            const orig = originalPipe?.[field];
            const curNum = cur != null ? Number(cur) : undefined;
            const origNum = orig != null ? Number(orig) : undefined;
            if (curNum !== origNum) return true;
          }
          // Compare pipe type, mode, sequence, inner diameter (API: pipeType, pipeMode, pipeSequence, pipeInnerDiameter)
          if ((currentPipe?.pipeType ?? '') !== (originalPipe?.pipeType ?? '')) return true;
          if ((currentPipe?.pipeMode ?? '') !== (originalPipe?.pipeMode ?? '')) return true;
          if (String(currentPipe?.pipeSequence ?? '').trim() !== String(originalPipe?.pipeSequence ?? '').trim()) return true;
          const curInner = currentPipe?.pipeInnerDiameter != null ? Number(currentPipe.pipeInnerDiameter) : undefined;
          const origInner = originalPipe?.pipeInnerDiameter != null ? Number(originalPipe.pipeInnerDiameter) : undefined;
          if (curInner !== origInner) return true;
          const pipefitterExtraFields = [
            'nominalPipeLength', 'targetNumberOfPipes', 'targetPipelineLength', 'weldSeamExclusionDeg',
            'nominalOd', 'nominalId', 'pipeMaterial', 'pipeRotation', 'hiloThreshold', 'nominalOdMin', 'nominalOdMax', 'nominalIdMin', 'nominalIdMax',
            'highlightType', 'oORIDTolerance', 'oORODTolerance',
            'oORIDMin', 'oORIDMax', 'oORODMin', 'oORODMax',
          ] as const;
          for (const field of pipefitterExtraFields) {
            const cur = currentPipe?.[field];
            const orig = originalPipe?.[field];
            const curVal = cur == null ? undefined : (typeof cur === 'string' ? String(cur).trim() : Number(cur));
            const origVal = orig == null ? undefined : (typeof orig === 'string' ? String(orig).trim() : Number(orig));
            if (curVal !== origVal) return true;
          }

          // Compare unit/units (unit selection dropdown changes)
          const currentUnit = (currentPipe as any)?.unit || (currentPipe as any)?.units || {};
          const originalUnit = (originalPipe as any)?.unit || (originalPipe as any)?.units || {};
          const unitFields = ['pipeSize', 'wallThickness', 'length', 'outputAssemblyLength'] as const;
          for (const f of unitFields) {
            const cu = currentUnit[f] ?? defaultUnit;
            const ou = originalUnit[f] ?? defaultUnit;
            if (cu !== ou) return true;
          }
          // Compare per-field pipefitter display units (e.g. nominalPipeLength, targetPipelineLength)
          const currentPfUnits: Record<string, string> = currentUnit.pipefitterFieldUnits ?? {};
          const originalPfUnits: Record<string, string> = originalUnit.pipefitterFieldUnits ?? {};
          const pfUnitKeys = Array.from(
            new Set([
              ...Object.keys(currentPfUnits),
              ...Object.keys(originalPfUnits),
              'nominalPipeLength',
              'targetPipelineLength',
            ])
          );
          for (const key of pfUnitKeys) {
            const currentDefault =
              key === 'nominalPipeLength' || key === 'targetPipelineLength' ? 'ft' : 'mm';
            const originalDefault =
              key === 'nominalPipeLength' || key === 'targetPipelineLength' ? 'ft' : 'mm';
            const cu = currentPfUnits[key] ?? currentDefault;
            const ou = originalPfUnits[key] ?? originalDefault;
            if (cu !== ou) return true;
          }
        }
      }
    }

    return false;
  }

  // Check if any state has pipes
  const hasAnyPipesInAnyState = (): boolean => {
    return getHasAnyPipes(statePipeline) ||
      getHasAnyPipes(stateBuckleArrestor) ||
      getHasAnyPipes(statePipeInPipe) ||
      getHasAnyPipes(stateBulkhead) ||
      getHasAnyPipes(stateSpools) ||
      getHasAnyPipes(stateOthers);
  }

  // Check if any state has valid pipes (with actual data)
  const hasAnyValidPipesInAnyState = (): boolean => {
    return hasValidPipesInState(statePipeline) ||
      hasValidPipesInState(stateBuckleArrestor) ||
      hasValidPipesInState(statePipeInPipe) ||
      hasValidPipesInState(stateBulkhead) ||
      hasValidPipesInState(stateSpools) ||
      hasValidPipesInState(stateOthers);
  }

  // Check if any state has numberOfPipeSizes greater than 0
  const hasAnyValidNumberOfPipeSizes = (): boolean => {
    return (Number(statePipeline.numberOfPipeSizes) > 0) ||
      (Number(stateBuckleArrestor.numberOfPipeSizes) > 0) ||
      (Number(statePipeInPipe.numberOfPipeSizes) > 0) ||
      (Number(stateBulkhead.numberOfPipeSizes) > 0) ||
      (Number(stateSpools.numberOfPipeSizes) > 0) ||
      (Number(stateOthers.numberOfPipeSizes) > 0);
  }

  // Validation function to check for empty or zero values
  const validateSetupData = (setupData: ProjectSetupData): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    // Check if we have any setup data at all
    const hasAnySetupData = Object.values(setupData).some((setup: any) =>
      setup && setup.pipeSizes && setup.pipeSizes.length > 0
    );

    if (!hasAnySetupData) {
      errors.push('At least one pipe size is required in any tab');
      return { isValid: false, errors };
    }

    // Validate each setup type that has data
    Object.entries(setupData).forEach(([_key, currentSetup]) => {
      if (!currentSetup || !currentSetup.pipeSizes || currentSetup.pipeSizes.length === 0) {
        return;
      }

      currentSetup.pipeSizes.forEach((pipe: any, index: number) => {
        const pipeNumber = index + 1;
        const label = pipe.pipeName ? `Pipe "${pipe.pipeName}"` : `Pipe ${pipeNumber}`;

        // Check pipe size
        if (!pipe.pipeSize || pipe.pipeSize === 0) {
          errors.push(`${label}: Pipe size is required`);
        }

        // Check wall thickness
        if (!pipe.wallThickness || pipe.wallThickness === 0) {
          errors.push(`${label}: Wall thickness is required and cannot be zero`);
        }

        const isSpoolsFabrication =
          currentSetup.projectType === 'fabrication' && currentSetup.projectSubType === 'spools';

        if (isSpoolsFabrication) {
          const spoolCount = getNumberOfSpoolsFromPipe(pipe as PipeSize1);
          if (spoolCount <= 0) {
            errors.push(`${label}: Number of spools is required and cannot be zero`);
          }
        } else {
          if (!pipe.numberOfPipes || pipe.numberOfPipes === 0) {
            errors.push(`${label}: Number of pipes is required and cannot be zero`);
          }

          if (!pipe.pipeLength || pipe.pipeLength === 0) {
            errors.push(`${label}: Pipe length is required and cannot be zero`);
          }
        }

        // Check manufacturer ID
        if (!pipe.manufacturerIds || pipe.manufacturerIds.length === 0) {
          errors.push(`${label}: Manufacturer is required`);
        }

        // Check buckle arrestors (only for buckleArrestor and bulkhead subtypes)
        if (currentSetup.projectType === 'fabrication' && (currentSetup.projectSubType === 'buckleArrestor' || currentSetup.projectSubType === 'bulkhead') && (pipe.numberOfBuckleArrestors === undefined || pipe.numberOfBuckleArrestors === null || pipe.numberOfBuckleArrestors === 0 || pipe.numberOfBuckleArrestors === '')) {
          errors.push(`${label}: Number of buckle arrestors is required and cannot be zero`);
        }

        // Check number of forges (only for buckleArrestor subtype)
        if (currentSetup.projectType === 'fabrication' && currentSetup.projectSubType === 'buckleArrestor' && (pipe.numberOfForges === undefined || pipe.numberOfForges === null || pipe.numberOfForges === 0 || pipe.numberOfForges === '')) {
          errors.push(`${label}: Number of forges is required and cannot be zero`);
        }

        // Check output assembly length (only for buckleArrestor and bulkhead subtypes)
        if (currentSetup.projectType === 'fabrication' && (currentSetup.projectSubType === 'buckleArrestor' || currentSetup.projectSubType === 'bulkhead') && (pipe.outputAssemblyLength === undefined || pipe.outputAssemblyLength === null || pipe.outputAssemblyLength === 0 || pipe.outputAssemblyLength === '')) {
          errors.push(`${label}: Output assembly length is required and cannot be zero`);
        }

        // Pipefitter configuration fields are optional — no required-field validation on save.
      });

      const shouldValidateJobNumbers =
        currentSetup.projectType === 'pipeline' ||
        (currentSetup.projectType === 'fabrication' && currentSetup.projectSubType !== 'spools');

      if (shouldValidateJobNumbers) {
        errors.push(...getCrossPipeDuplicateJobNumberErrors(currentSetup.pipeSizes));
      }
    });

    return { isValid: errors.length === 0, errors };
  };

  // Get current setup data from all available tabs using separate state
  const getCurrentSetupData = (): ProjectSetupData => {
    const result: ProjectSetupData = {};

    // Get pipeline data from state
    if (project.project.projectTypes.pipeline?.enabled) {
      const pipelineKey = String(project.project.projectTypes.pipeline?.project_type_id);

      // Preserve existing data if available
      const existingData = setupData[pipelineKey];
      if (statePipeline.pipeSizes && statePipeline.pipeSizes.length > 0) {
        result[pipelineKey] = {
          projectId: project.project.projectId ? parseInt(project.project.projectId) : 1,
          projectTypeId: project.project.projectTypes.pipeline?.project_type_id || 12,
          projectType: "pipeline",
          projectSubType: "none",
          numberOfPipeSizes: statePipeline.pipeSizes.length,
          pipeSizes: statePipeline.pipeSizes.map((pipe: any, index: number) => {
            // Find existing pipe data to preserve IDs and counts
            const existingPipe = existingData?.pipeSizes?.find((ep: any) =>
              ep.pipeId === pipe.pipeId || ep.pipeName === pipe.pipeName
            );

            // Convert user input from display units to inches for DB (always stores inches)
            const pipeToSave = {
              pipeId: pipe.pipeId || null,
              pipeName: pipe.pipeName || `Pipe ${index + 1}`,
              pipeSize: Number(pipe.pipeSize) || 0,
              wallThickness: Number(pipe.wallThickness) || 0,
              numberOfBuckleArrestors: Number(pipe.numberOfBuckleArrestors) || 0,
              numberOfSpools: Number(pipe.numberOfSpools) || 0,
              numberOfPipes: Number(pipe.numberOfPipes) || 0,
              pipeLength: Number(pipe.pipeLength) || 0,
              numberOfForges: Number(pipe.numberOfForges) || 0,
              outputAssemblyLength: Number(pipe.outputAssemblyLength) || 0,
              manufacturerId: pipe.manufacturerId || 0,
              manufacturerIds: pipe.manufacturerIds || [],
              pipeOrder: index + 1,
              isActive: true,
              prefixAssemblyName: pipe.prefixAssemblyName || 'Assy',
              groupId: pipe.groupId ?? '',
              jobNumber: pipe?.jobNumber || { numberOfJobs: 1, jobNumbers: [''] },
              pipeDetailsExtraDetails: pipe.pipeDetailsExtraDetails || {},
              units: pipe.units || { pipeSize: defaultUnit, wallThickness: defaultUnit, length: defaultUnit, outputAssemblyLength: 'ft' },
              documentsData: pipe.documentsData || existingPipe?.documentsData || {
                numberOfDocs: 0, isDocumentsAvailable: false, documentIds: [], documentFileNames: []
              },
              toeSeamSeparationMinMm: pipe.toeSeamSeparationMinMm,
              seamCapWidthMaxMm: pipe.seamCapWidthMaxMm,
              sixOclockExclusionDeg: pipe.sixOclockExclusionDeg,
              hiloWpsLimitMm: pipe.hiloWpsLimitMm,
              excessiveHiloLengthLimitDeg: pipe.excessiveHiloLengthLimitDeg,
              hiloPreferredLimitTopMm: pipe.hiloPreferredLimitTopMm,
              hiloPreferredLimitMiddleMm: pipe.hiloPreferredLimitMiddleMm,
              hiloPreferredLimitLowerMm: pipe.hiloPreferredLimitLowerMm,
              pipeType: pipe.pipeType,
              pipeMode: pipe.pipeMode,
              pipeSequence: pipe.pipeSequence != null && String(pipe.pipeSequence).trim() !== '' ? String(pipe.pipeSequence) : undefined,
              pipeInnerDiameter: pipe.pipeInnerDiameter != null ? Number(pipe.pipeInnerDiameter) : undefined,
              nominalPipeLength: pipe.nominalPipeLength != null ? Number(pipe.nominalPipeLength) : undefined,
              targetNumberOfPipes: pipe.targetNumberOfPipes != null ? Number(pipe.targetNumberOfPipes) : undefined,
              targetPipelineLength: pipe.targetPipelineLength != null ? Number(pipe.targetPipelineLength) : undefined,
              weldSeamExclusionDeg: pipe.weldSeamExclusionDeg != null ? Number(pipe.weldSeamExclusionDeg) : undefined,
              nominalOd: pipe.nominalOd != null ? Number(pipe.nominalOd) : undefined,
              pipeMaterial: pipe.pipeMaterial ?? undefined,
              pipeRotation: pipe.pipeRotation ?? undefined,
              hiloThreshold: hiloThresholdFromPipe(pipe),
              nominalOdMin: pipe.nominalOdMin != null ? Number(pipe.nominalOdMin) : undefined,
              nominalOdMax: pipe.nominalOdMax != null ? Number(pipe.nominalOdMax) : undefined,
              nominalIdMin: pipe.nominalIdMin != null ? Number(pipe.nominalIdMin) : undefined,
              nominalIdMax: pipe.nominalIdMax != null ? Number(pipe.nominalIdMax) : undefined,
              oORIDMin: pipe.oORIDMin != null ? Number(pipe.oORIDMin) : undefined,
              oORIDMax: pipe.oORIDMax != null ? Number(pipe.oORIDMax) : undefined,
              oORODMin: pipe.oORODMin != null ? Number(pipe.oORODMin) : undefined,
              oORODMax: pipe.oORODMax != null ? Number(pipe.oORODMax) : undefined,
              nominalId: pipe.nominalId != null ? Number(pipe.nominalId) : undefined,
              highlightType:
                pipe.highlightType === 'Range'
                  ? 'Range'
                  : pipe.highlightType != null && String(pipe.highlightType).trim() !== ''
                    ? 'tolerance'
                    : undefined,
              oORIDTolerance: numberOrUndefined(
                pipe.oORIDTolerance ?? pipe.oORIDTolrence
              ),
              oORODTolerance: numberOrUndefined(
                pipe.oORODTolerance ?? pipe.oORODTolrence
              ),
            };
            return convertPipefitterFieldsToMm(convertSetupPipeToInchesForSave(pipeToSave));
          })
        };
      }
    }

    // Get fabrication data from separate states
    if (project.project.projectTypes.fabrication?.enabled) {
      // Handle buckleArrestor data
      if (stateBuckleArrestor.pipeSizes && stateBuckleArrestor.pipeSizes.length > 0) {
        const buckleArrestorKey = String(project.project.projectTypes.fabrication?.options?.buckleArrestor?.project_type_id);
        const existingData = setupData[buckleArrestorKey];

        result[buckleArrestorKey] = {
          projectId: project.project.projectId ? parseInt(project.project.projectId) : 1,
          projectTypeId: project.project.projectTypes.fabrication?.options?.buckleArrestor?.project_type_id || 0,
          projectType: "fabrication",
          projectSubType: "buckleArrestor",
          numberOfPipeSizes: stateBuckleArrestor.pipeSizes.length,
          pipeSizes: stateBuckleArrestor.pipeSizes.length > 0 ? stateBuckleArrestor.pipeSizes.map((pipe: any, index: number) => {
            // Find existing pipe data to preserve IDs and counts
            const existingPipe = existingData?.pipeSizes?.find((ep: any) =>
              ep.pipeId === pipe.pipeId || ep.pipeName === pipe.pipeName
            );

            // Convert user input from display units to inches for DB (always stores inches)
            const pipeToSave = {
              pipeId: pipe.pipeId || null,
              pipeName: pipe.pipeName || `Pipe ${index + 1}`,
              pipeSize: Number(pipe.pipeSize) || 0,
              wallThickness: Number(pipe.wallThickness) || 0,
              numberOfBuckleArrestors: Number(pipe.numberOfBuckleArrestors) || 0,
              numberOfSpools: Number(pipe.numberOfSpools) || 0,
              numberOfPipes: Number(pipe.numberOfPipes) || 0,
              pipeLength: Number(pipe.pipeLength) || 0,
              numberOfForges: Number(pipe.numberOfForges) || 0,
              outputAssemblyLength: Number(pipe.outputAssemblyLength) || 0,
              manufacturerId: pipe.manufacturerId || 0,
              manufacturerIds: pipe.manufacturerIds || [],
              pipeOrder: index + 1,
              isActive: true,
              prefixAssemblyName: pipe.prefixAssemblyName || 'Assy',
              groupId: pipe.groupId ?? '',
              jobNumber: pipe?.jobNumber || { numberOfJobs: 1, jobNumbers: [''] },
              pipeDetailsExtraDetails: pipe.pipeDetailsExtraDetails || {},
              units: pipe.units || { pipeSize: defaultUnit, wallThickness: defaultUnit, length: defaultUnit, outputAssemblyLength: 'ft' },
              documentsData: pipe.documentsData || existingPipe?.documentsData || {
                numberOfDocs: 0, isDocumentsAvailable: false, documentIds: [], documentFileNames: []
              },
              toeSeamSeparationMinMm: pipe.toeSeamSeparationMinMm,
              seamCapWidthMaxMm: pipe.seamCapWidthMaxMm,
              sixOclockExclusionDeg: pipe.sixOclockExclusionDeg,
              hiloWpsLimitMm: pipe.hiloWpsLimitMm,
              excessiveHiloLengthLimitDeg: pipe.excessiveHiloLengthLimitDeg,
              hiloPreferredLimitTopMm: pipe.hiloPreferredLimitTopMm,
              hiloPreferredLimitMiddleMm: pipe.hiloPreferredLimitMiddleMm,
              hiloPreferredLimitLowerMm: pipe.hiloPreferredLimitLowerMm,
              pipeType: pipe.pipeType,
              pipeMode: pipe.pipeMode,
              pipeSequence: pipe.pipeSequence != null && String(pipe.pipeSequence).trim() !== '' ? String(pipe.pipeSequence) : undefined,
              pipeInnerDiameter: pipe.pipeInnerDiameter != null ? Number(pipe.pipeInnerDiameter) : undefined,
              nominalPipeLength: pipe.nominalPipeLength != null ? Number(pipe.nominalPipeLength) : undefined,
              targetNumberOfPipes: pipe.targetNumberOfPipes != null ? Number(pipe.targetNumberOfPipes) : undefined,
              targetPipelineLength: pipe.targetPipelineLength != null ? Number(pipe.targetPipelineLength) : undefined,
              weldSeamExclusionDeg: pipe.weldSeamExclusionDeg != null ? Number(pipe.weldSeamExclusionDeg) : undefined,
              nominalOd: pipe.nominalOd != null ? Number(pipe.nominalOd) : undefined,
              pipeMaterial: pipe.pipeMaterial ?? undefined,
              pipeRotation: pipe.pipeRotation ?? undefined,
              hiloThreshold: hiloThresholdFromPipe(pipe),
              nominalOdMin: pipe.nominalOdMin != null ? Number(pipe.nominalOdMin) : undefined,
              nominalOdMax: pipe.nominalOdMax != null ? Number(pipe.nominalOdMax) : undefined,
              nominalIdMin: pipe.nominalIdMin != null ? Number(pipe.nominalIdMin) : undefined,
              nominalIdMax: pipe.nominalIdMax != null ? Number(pipe.nominalIdMax) : undefined,
              oORIDMin: pipe.oORIDMin != null ? Number(pipe.oORIDMin) : undefined,
              oORIDMax: pipe.oORIDMax != null ? Number(pipe.oORIDMax) : undefined,
              oORODMin: pipe.oORODMin != null ? Number(pipe.oORODMin) : undefined,
              oORODMax: pipe.oORODMax != null ? Number(pipe.oORODMax) : undefined,
              nominalId: pipe.nominalId != null ? Number(pipe.nominalId) : undefined,
              highlightType:
                pipe.highlightType === 'Range'
                  ? 'Range'
                  : pipe.highlightType != null && String(pipe.highlightType).trim() !== ''
                    ? 'tolerance'
                    : undefined,
              oORIDTolerance: numberOrUndefined(
                pipe.oORIDTolerance ?? pipe.oORIDTolrence
              ),
              oORODTolerance: numberOrUndefined(
                pipe.oORODTolerance ?? pipe.oORODTolrence
              ),
            };
            return convertPipefitterFieldsToMm(convertSetupPipeToInchesForSave(pipeToSave));
          }) : []
        };
      }

      // Handle pipeInPipe data
      if (statePipeInPipe.pipeSizes && statePipeInPipe.pipeSizes.length > 0) {
        const pipeInPipeKey = String(project.project.projectTypes.fabrication?.options?.pipeInPipe?.project_type_id);
        const existingData = setupData[pipeInPipeKey];

        result[pipeInPipeKey] = {
          projectId: project.project.projectId ? parseInt(project.project.projectId) : 1,
          projectTypeId: project.project.projectTypes.fabrication?.options?.pipeInPipe?.project_type_id || 0,
          projectType: "fabrication",
          projectSubType: "pipeInPipe",
          numberOfPipeSizes: statePipeInPipe.pipeSizes.length,
          pipeSizes: statePipeInPipe.pipeSizes.map((pipe: any, index: number) => {
            // Find existing pipe data to preserve IDs and counts
            const existingPipe = existingData?.pipeSizes?.find((ep: any) =>
              ep.pipeId === pipe.pipeId || ep.pipeName === pipe.pipeName
            );

            // Convert user input from display units to inches for DB (always stores inches)
            const pipeToSave = {
              pipeId: pipe.pipeId || null,
              pipeName: pipe.pipeName || `Pipe ${index + 1}`,
              pipeSize: Number(pipe.pipeSize) || 0,
              wallThickness: Number(pipe.wallThickness) || 0,
              numberOfBuckleArrestors: Number(pipe.numberOfSpools ?? pipe.numberOfBuckleArrestors) || 0,
              numberOfSpools: Number(pipe.numberOfSpools ?? pipe.numberOfBuckleArrestors) || 0,
              numberOfPipes: Number(pipe.numberOfPipes) || 0,
              pipeLength: Number(pipe.pipeLength) || 0,
              numberOfForges: Number(pipe.numberOfForges) || 0,
              outputAssemblyLength: Number(pipe.outputAssemblyLength) || 0,
              manufacturerId: pipe.manufacturerId || 0,
              manufacturerIds: pipe.manufacturerIds || [],
              pipeOrder: index + 1,
              isActive: true,
              prefixAssemblyName: pipe.prefixAssemblyName || 'Assy',
              groupId: pipe.groupId ?? '',
              jobNumber: pipe?.jobNumber || { numberOfJobs: 1, jobNumbers: [''] },
              pipeDetailsExtraDetails: pipe.pipeDetailsExtraDetails || {},
              units: pipe.units || { pipeSize: defaultUnit, wallThickness: defaultUnit, length: defaultUnit, outputAssemblyLength: 'ft' },
              documentsData: pipe.documentsData || existingPipe?.documentsData || {
                numberOfDocs: 0, isDocumentsAvailable: false, documentIds: [], documentFileNames: []
              },
              toeSeamSeparationMinMm: pipe.toeSeamSeparationMinMm,
              seamCapWidthMaxMm: pipe.seamCapWidthMaxMm,
              sixOclockExclusionDeg: pipe.sixOclockExclusionDeg,
              hiloWpsLimitMm: pipe.hiloWpsLimitMm,
              excessiveHiloLengthLimitDeg: pipe.excessiveHiloLengthLimitDeg,
              hiloPreferredLimitTopMm: pipe.hiloPreferredLimitTopMm,
              hiloPreferredLimitMiddleMm: pipe.hiloPreferredLimitMiddleMm,
              hiloPreferredLimitLowerMm: pipe.hiloPreferredLimitLowerMm,
              pipeType: pipe.pipeType,
              pipeMode: pipe.pipeMode,
              pipeSequence: pipe.pipeSequence != null && String(pipe.pipeSequence).trim() !== '' ? String(pipe.pipeSequence) : undefined,
              pipeInnerDiameter: pipe.pipeInnerDiameter != null ? Number(pipe.pipeInnerDiameter) : undefined,
              nominalPipeLength: pipe.nominalPipeLength != null ? Number(pipe.nominalPipeLength) : undefined,
              targetNumberOfPipes: pipe.targetNumberOfPipes != null ? Number(pipe.targetNumberOfPipes) : undefined,
              targetPipelineLength: pipe.targetPipelineLength != null ? Number(pipe.targetPipelineLength) : undefined,
              weldSeamExclusionDeg: pipe.weldSeamExclusionDeg != null ? Number(pipe.weldSeamExclusionDeg) : undefined,
              nominalOd: pipe.nominalOd != null ? Number(pipe.nominalOd) : undefined,
              pipeMaterial: pipe.pipeMaterial ?? undefined,
              pipeRotation: pipe.pipeRotation ?? undefined,
              hiloThreshold: hiloThresholdFromPipe(pipe),
              nominalOdMin: pipe.nominalOdMin != null ? Number(pipe.nominalOdMin) : undefined,
              nominalOdMax: pipe.nominalOdMax != null ? Number(pipe.nominalOdMax) : undefined,
              nominalIdMin: pipe.nominalIdMin != null ? Number(pipe.nominalIdMin) : undefined,
              nominalIdMax: pipe.nominalIdMax != null ? Number(pipe.nominalIdMax) : undefined,
              oORIDMin: pipe.oORIDMin != null ? Number(pipe.oORIDMin) : undefined,
              oORIDMax: pipe.oORIDMax != null ? Number(pipe.oORIDMax) : undefined,
              oORODMin: pipe.oORODMin != null ? Number(pipe.oORODMin) : undefined,
              oORODMax: pipe.oORODMax != null ? Number(pipe.oORODMax) : undefined,
              nominalId: pipe.nominalId != null ? Number(pipe.nominalId) : undefined,
              highlightType:
                pipe.highlightType === 'Range'
                  ? 'Range'
                  : pipe.highlightType != null && String(pipe.highlightType).trim() !== ''
                    ? 'tolerance'
                    : undefined,
              oORIDTolerance: numberOrUndefined(
                pipe.oORIDTolerance ?? pipe.oORIDTolrence
              ),
              oORODTolerance: numberOrUndefined(
                pipe.oORODTolerance ?? pipe.oORODTolrence
              ),
            };
            return convertPipefitterFieldsToMm(convertSetupPipeToInchesForSave(pipeToSave));
          })
        };
      }

      // Handle bulkhead data
      if (stateBulkhead.pipeSizes && stateBulkhead.pipeSizes.length > 0) {
        const bulkheadKey = String(project.project.projectTypes.fabrication?.options?.bulkhead?.project_type_id);
        const existingData = setupData[bulkheadKey];

        result[bulkheadKey] = {
          projectId: project.project.projectId ? parseInt(project.project.projectId) : 1,
          projectTypeId: project.project.projectTypes.fabrication?.options?.bulkhead?.project_type_id || 0,
          projectType: "fabrication",
          projectSubType: "bulkhead",
          numberOfPipeSizes: stateBulkhead.pipeSizes.length,
          pipeSizes: stateBulkhead.pipeSizes.map((pipe: any, index: number) => {
            // Find existing pipe data to preserve IDs and counts
            const existingPipe = existingData?.pipeSizes?.find((ep: any) =>
              ep.pipeId === pipe.pipeId || ep.pipeName === pipe.pipeName
            );

            // Convert user input from display units to inches for DB (always stores inches)
            const pipeToSave = {
              pipeId: pipe.pipeId || null,
              pipeName: pipe.pipeName || `Pipe ${index + 1}`,
              pipeSize: Number(pipe.pipeSize) || 0,
              wallThickness: Number(pipe.wallThickness) || 0,
              numberOfBuckleArrestors: Number(pipe.numberOfBuckleArrestors) || 0,
              numberOfSpools: Number(pipe.numberOfSpools) || 0,
              numberOfPipes: Number(pipe.numberOfPipes) || 0,
              pipeLength: Number(pipe.pipeLength) || 0,
              numberOfForges: Number(pipe.numberOfForges) || 0,
              outputAssemblyLength: Number(pipe.outputAssemblyLength) || 0,
              manufacturerId: pipe.manufacturerId || 0,
              manufacturerIds: pipe.manufacturerIds || [],
              pipeOrder: index + 1,
              isActive: true,
              prefixAssemblyName: pipe.prefixAssemblyName || 'Assy',
              groupId: pipe.groupId ?? '',
              jobNumber: pipe?.jobNumber || { numberOfJobs: 1, jobNumbers: [''] },
              pipeDetailsExtraDetails: pipe.pipeDetailsExtraDetails || {},
              units: pipe.units || { pipeSize: defaultUnit, wallThickness: defaultUnit, length: defaultUnit, outputAssemblyLength: 'ft' },
              documentsData: pipe.documentsData || existingPipe?.documentsData || {
                numberOfDocs: 0, isDocumentsAvailable: false, documentIds: [], documentFileNames: []
              },
              toeSeamSeparationMinMm: pipe.toeSeamSeparationMinMm,
              seamCapWidthMaxMm: pipe.seamCapWidthMaxMm,
              sixOclockExclusionDeg: pipe.sixOclockExclusionDeg,
              hiloWpsLimitMm: pipe.hiloWpsLimitMm,
              excessiveHiloLengthLimitDeg: pipe.excessiveHiloLengthLimitDeg,
              hiloPreferredLimitTopMm: pipe.hiloPreferredLimitTopMm,
              hiloPreferredLimitMiddleMm: pipe.hiloPreferredLimitMiddleMm,
              hiloPreferredLimitLowerMm: pipe.hiloPreferredLimitLowerMm,
              pipeType: pipe.pipeType,
              pipeMode: pipe.pipeMode,
              pipeSequence: pipe.pipeSequence != null && String(pipe.pipeSequence).trim() !== '' ? String(pipe.pipeSequence) : undefined,
              pipeInnerDiameter: pipe.pipeInnerDiameter != null ? Number(pipe.pipeInnerDiameter) : undefined,
              nominalPipeLength: pipe.nominalPipeLength != null ? Number(pipe.nominalPipeLength) : undefined,
              targetNumberOfPipes: pipe.targetNumberOfPipes != null ? Number(pipe.targetNumberOfPipes) : undefined,
              targetPipelineLength: pipe.targetPipelineLength != null ? Number(pipe.targetPipelineLength) : undefined,
              weldSeamExclusionDeg: pipe.weldSeamExclusionDeg != null ? Number(pipe.weldSeamExclusionDeg) : undefined,
              nominalOd: pipe.nominalOd != null ? Number(pipe.nominalOd) : undefined,
              pipeMaterial: pipe.pipeMaterial ?? undefined,
              pipeRotation: pipe.pipeRotation ?? undefined,
              hiloThreshold: hiloThresholdFromPipe(pipe),
              nominalOdMin: pipe.nominalOdMin != null ? Number(pipe.nominalOdMin) : undefined,
              nominalOdMax: pipe.nominalOdMax != null ? Number(pipe.nominalOdMax) : undefined,
              nominalIdMin: pipe.nominalIdMin != null ? Number(pipe.nominalIdMin) : undefined,
              nominalIdMax: pipe.nominalIdMax != null ? Number(pipe.nominalIdMax) : undefined,
              oORIDMin: pipe.oORIDMin != null ? Number(pipe.oORIDMin) : undefined,
              oORIDMax: pipe.oORIDMax != null ? Number(pipe.oORIDMax) : undefined,
              oORODMin: pipe.oORODMin != null ? Number(pipe.oORODMin) : undefined,
              oORODMax: pipe.oORODMax != null ? Number(pipe.oORODMax) : undefined,
              nominalId: pipe.nominalId != null ? Number(pipe.nominalId) : undefined,
              highlightType:
                pipe.highlightType === 'Range'
                  ? 'Range'
                  : pipe.highlightType != null && String(pipe.highlightType).trim() !== ''
                    ? 'tolerance'
                    : undefined,
              oORIDTolerance: numberOrUndefined(
                pipe.oORIDTolerance ?? pipe.oORIDTolrence
              ),
              oORODTolerance: numberOrUndefined(
                pipe.oORODTolerance ?? pipe.oORODTolrence
              ),
            };
            return convertPipefitterFieldsToMm(convertSetupPipeToInchesForSave(pipeToSave));
          })
        };
      }

      // Handle spools data
      if (stateSpools.pipeSizes && stateSpools.pipeSizes.length > 0) {
        const spoolsKey = String(project.project.projectTypes.fabrication?.options?.spools?.project_type_id);
        const existingData = setupData[spoolsKey];

        result[spoolsKey] = {
          projectId: project.project.projectId ? parseInt(project.project.projectId) : 1,
          projectTypeId: project.project.projectTypes.fabrication?.options?.spools?.project_type_id || 0,
          projectType: "fabrication",
          projectSubType: "spools",
          numberOfPipeSizes: stateSpools.pipeSizes.length,
          pipeSizes: stateSpools.pipeSizes.map((pipe: any, index: number) => {
            const existingPipe = existingData?.pipeSizes?.find((ep: any) =>
              ep.pipeId === pipe.pipeId || ep.pipeName === pipe.pipeName
            );
            const spoolCount = getNumberOfSpoolsFromPipe(pipe as PipeSize1 & Record<string, unknown>);
            const spoolRows = normalizeSpoolDetailsRows(
              spoolCount,
              getSpoolDetailsFromPipe(pipe as PipeSize1)
            );
            const baseExtra =
              pipe.pipeDetailsExtraDetails && typeof pipe.pipeDetailsExtraDetails === 'object'
                ? { ...(pipe.pipeDetailsExtraDetails as Record<string, unknown>) }
                : {};
            // Spool rows live on `spoolDetails` only — do not persist them under `pipeDetailsExtraDetails`.
            delete (baseExtra as Record<string, unknown>).spoolDetails;
            const pipeToSave = {
              pipeId: pipe.pipeId || null,
              pipeName: `Pipe ${index + 1}`,
              pipeSize: Number(pipe.pipeSize) || 0,
              wallThickness: Number(pipe.wallThickness) || 0,
              numberOfBuckleArrestors: Number(pipe.numberOfBuckleArrestors) || 0,
              numberOfSpools: Number(pipe.numberOfSpools) || 0,
              numberOfPipes: Number(pipe.numberOfPipes) || 0,
              pipeLength: Number(pipe.pipeLength) || 0,
              numberOfForges: Number(pipe.numberOfForges) || 0,
              outputAssemblyLength: Number(pipe.outputAssemblyLength) || 0,
              manufacturerId: pipe.manufacturerId || 0,
              manufacturerIds: pipe.manufacturerIds || [],
              pipeOrder: index + 1,
              isActive: true,
              prefixAssemblyName: pipe.prefixAssemblyName || 'Assy',
              groupId: pipe.groupId ?? '',
              jobNumber: pipe?.jobNumber || { numberOfJobs: 1, jobNumbers: [''] },
              pipeDetailsExtraDetails: baseExtra,
              spoolDetails: buildSpoolDetailsPayload(spoolCount, spoolRows),
              units: pipe.units || { pipeSize: defaultUnit, wallThickness: defaultUnit, length: defaultUnit, outputAssemblyLength: 'ft' },
              documentsData: pipe.documentsData || existingPipe?.documentsData || {
                numberOfDocs: 0, isDocumentsAvailable: false, documentIds: [], documentFileNames: []
              }
            };
            return convertSetupPipeToInchesForSave(pipeToSave);
          })
        };
      }
    }

    // Get others data from state
    if (project.project.projectTypes.others?.enabled) {
      const othersKey = String(project.project.projectTypes.others?.project_type_id || 13);

      // Preserve existing data if available
      const existingData = setupData[othersKey];

      if (stateOthers.pipeSizes && stateOthers.pipeSizes.length > 0) {
        result[othersKey] = {
          projectId: project.project.projectId ? parseInt(project.project.projectId) : 1,
          projectTypeId: project.project.projectTypes.others?.project_type_id || 13,
          projectType: "others",
          projectSubType: "none",
          numberOfPipeSizes: stateOthers.pipeSizes.length,
          pipeSizes: stateOthers.pipeSizes.map((pipe: any, index: number) => {
            // Find existing pipe data to preserve IDs and counts
            const existingPipe = existingData?.pipeSizes?.find((ep: any) =>
              ep.pipeId === pipe.pipeId || ep.pipeName === pipe.pipeName
            );

            // Convert user input from display units to inches for DB (always stores inches)
            const pipeToSave = {
              pipeId: pipe.pipeId || null,
              pipeName: pipe.pipeName || `Pipe ${index + 1}`,
              pipeSize: Number(pipe.pipeSize) || 0,
              wallThickness: Number(pipe.wallThickness) || 0,
              numberOfBuckleArrestors: Number(pipe.numberOfSpools ?? pipe.numberOfBuckleArrestors) || 0,
              numberOfSpools: Number(pipe.numberOfSpools ?? pipe.numberOfBuckleArrestors) || 0,
              numberOfPipes: Number(pipe.numberOfPipes) || 0,
              pipeLength: Number(pipe.pipeLength) || 0,
              numberOfForges: Number(pipe.numberOfForges) || 0,
              outputAssemblyLength: Number(pipe.outputAssemblyLength) || 0,
              manufacturerId: pipe.manufacturerId || 0,
              manufacturerIds: pipe.manufacturerIds || [],
              pipeOrder: index + 1,
              isActive: true,
              prefixAssemblyName: pipe.prefixAssemblyName || 'Assy',
              groupId: pipe.groupId ?? '',
              jobNumber: pipe?.jobNumber || { numberOfJobs: 1, jobNumbers: [''] },
              pipeDetailsExtraDetails: pipe.pipeDetailsExtraDetails || {},
              units: pipe.units || { pipeSize: defaultUnit, wallThickness: defaultUnit, length: defaultUnit, outputAssemblyLength: 'ft' },
              documentsData: pipe.documentsData || existingPipe?.documentsData || {
                numberOfDocs: 0, isDocumentsAvailable: false, documentIds: [], documentFileNames: []
              },
              toeSeamSeparationMinMm: pipe.toeSeamSeparationMinMm,
              seamCapWidthMaxMm: pipe.seamCapWidthMaxMm,
              sixOclockExclusionDeg: pipe.sixOclockExclusionDeg,
              hiloWpsLimitMm: pipe.hiloWpsLimitMm,
              excessiveHiloLengthLimitDeg: pipe.excessiveHiloLengthLimitDeg,
              hiloPreferredLimitTopMm: pipe.hiloPreferredLimitTopMm,
              hiloPreferredLimitMiddleMm: pipe.hiloPreferredLimitMiddleMm,
              hiloPreferredLimitLowerMm: pipe.hiloPreferredLimitLowerMm,
              pipeType: pipe.pipeType,
              pipeMode: pipe.pipeMode,
              pipeSequence: pipe.pipeSequence != null && String(pipe.pipeSequence).trim() !== '' ? String(pipe.pipeSequence) : undefined,
              pipeInnerDiameter: pipe.pipeInnerDiameter != null ? Number(pipe.pipeInnerDiameter) : undefined,
              nominalPipeLength: pipe.nominalPipeLength != null ? Number(pipe.nominalPipeLength) : undefined,
              targetNumberOfPipes: pipe.targetNumberOfPipes != null ? Number(pipe.targetNumberOfPipes) : undefined,
              targetPipelineLength: pipe.targetPipelineLength != null ? Number(pipe.targetPipelineLength) : undefined,
              weldSeamExclusionDeg: pipe.weldSeamExclusionDeg != null ? Number(pipe.weldSeamExclusionDeg) : undefined,
              nominalOd: pipe.nominalOd != null ? Number(pipe.nominalOd) : undefined,
              pipeMaterial: pipe.pipeMaterial ?? undefined,
              pipeRotation: pipe.pipeRotation ?? undefined,
              hiloThreshold: hiloThresholdFromPipe(pipe),
              nominalOdMin: pipe.nominalOdMin != null ? Number(pipe.nominalOdMin) : undefined,
              nominalOdMax: pipe.nominalOdMax != null ? Number(pipe.nominalOdMax) : undefined,
              nominalIdMin: pipe.nominalIdMin != null ? Number(pipe.nominalIdMin) : undefined,
              nominalIdMax: pipe.nominalIdMax != null ? Number(pipe.nominalIdMax) : undefined,
              oORIDMin: pipe.oORIDMin != null ? Number(pipe.oORIDMin) : undefined,
              oORIDMax: pipe.oORIDMax != null ? Number(pipe.oORIDMax) : undefined,
              oORODMin: pipe.oORODMin != null ? Number(pipe.oORODMin) : undefined,
              oORODMax: pipe.oORODMax != null ? Number(pipe.oORODMax) : undefined,
              nominalId: pipe.nominalId != null ? Number(pipe.nominalId) : undefined,
              highlightType:
                pipe.highlightType === 'Range'
                  ? 'Range'
                  : pipe.highlightType != null && String(pipe.highlightType).trim() !== ''
                    ? 'tolerance'
                    : undefined,
              oORIDTolerance: numberOrUndefined(
                pipe.oORIDTolerance ?? pipe.oORIDTolrence
              ),
              oORODTolerance: numberOrUndefined(
                pipe.oORODTolerance ?? pipe.oORODTolrence
              ),
            };
            return convertPipefitterFieldsToMm(convertSetupPipeToInchesForSave(pipeToSave));
          })
        };
      }
    }

    return result;
  };

  // Function to restore setup data in ProjectSetupData format
  const restoreSetupData = (data: ProjectSetupData): void => {
    setSetupData(data);

    // The child components will automatically use the setupData prop
    // which is passed down to them, so no need to call restore methods
  };

  // Update setup data when pipeDetails changes from Redux
  useEffect(() => {
    if (pipeDetails && Object.keys(pipeDetails).length > 0) {
      setSetupData(pipeDetails);

      // Check if we have existing data to determine edit mode
      const hasExistingData = Object.values(pipeDetails).some((setup: any) =>
        setup.pipeSizes && setup.pipeSizes.length > 0 &&
        setup.pipeSizes.some((pipe: any) => pipe.pipeId)
      );
      setEditMode(hasExistingData);
    } else if (pipeDetails && Object.keys(pipeDetails).length === 0 && dataLoaded) {
      // Only set empty data if we've already loaded (to avoid overwriting before initial load)
      setSetupData({});
      setOriginalSetupData({});
      baselineSyncNeededRef.current = false;
      setHasUnsavedChanges(false);
      OnChanged(false);
      setEditMode(false);
      // dispatch(getAllPipeDetails({ projectId: project.project.projectId }));
    }
  }, [pipeDetails, dataLoaded]);

  // Update manufacturing list when manufacturingCategories changes from Redux
  useEffect(() => {
    if (manufacturingCategories && manufacturingCategories.length > 0) {
      // Remove duplicates and sort alphabetically by manufacturer_name
      const uniqueManufacturing = manufacturingCategories
        .filter((manufacturing: any, index: number, self: any[]) =>
          index === self.findIndex((m: any) => m.manufacturer_name === manufacturing.manufacturer_name)
        )
        .sort((a: any, b: any) => a.manufacturer_name.localeCompare(b.manufacturer_name));

      setManufacturingList(uniqueManufacturing || []);
    }
  }, [manufacturingCategories]);

  // Update loading state based on Redux
  useEffect(() => {
    setIsLoadingData(isPipeDetailsLoading || isManufacturingCategoryLoading);
    // Mark data as loaded when loading completes
    if (!isPipeDetailsLoading && !isManufacturingCategoryLoading && !dataLoaded) {
      setDataLoaded(true);
    }
  }, [isPipeDetailsLoading, isManufacturingCategoryLoading, dataLoaded]);

  const loadInitialData = async () => {
    try {
      setIsLoadingData(true);

      // Use store data if available, don't call API on initial load
      // Only fetch if pipeDetails is completely null/undefined (not just empty)
      if (pipeDetails === null || pipeDetails === undefined) {
        // Dispatch Redux action to fetch pipe details only if store is empty
        dispatch(getAllPipeDetails({ projectId: project.project.projectId }));
      }

      // Use store data if available, only fetch if manufacturingCategories is null/undefined
      if (manufacturingCategories === null || manufacturingCategories === undefined || manufacturingCategories.length === 0) {
        // Dispatch Redux action to fetch manufacturing categories only if store is empty
        dispatch(getListManufacturingCategory({}));
      }
      // Manufacturing list will be updated via useEffect when manufacturingCategories changes
      // Reset unsaved changes after initial data load
      setHasUnsavedChanges(false);
      OnChanged(false);

    } catch (error) {
      setManufacturingList([]);
      setIsLoadingData(false);
      setDataLoaded(true);
      // Reset unsaved changes after initial data load (even on error)
      setHasUnsavedChanges(false);
      OnChanged(false);
    }
  };


  useEffect(() => {
    // Only load data once when component mounts
    if (!dataLoaded) {
      loadInitialData();
    } else {
    }
  }, [dataLoaded]);

  const updateOriginalPipeBaseline = (projectTypeId: number | string, pipeIndex: number) => {
    const key = String(projectTypeId);
    const current = getCurrentSetupData();
    const savedPipe = current[key]?.pipeSizes?.[pipeIndex];
    if (!savedPipe) return;

    setOriginalSetupData((prev: ProjectSetupData) => {
      const next = JSON.parse(JSON.stringify(prev)) as ProjectSetupData;
      if (!next[key]) {
        next[key] = JSON.parse(JSON.stringify(current[key]));
      }
      if (!next[key].pipeSizes) {
        next[key].pipeSizes = [];
      }
      while (next[key].pipeSizes.length <= pipeIndex) {
        next[key].pipeSizes.push({} as PipeSize1);
      }
      next[key].pipeSizes[pipeIndex] = JSON.parse(JSON.stringify(savedPipe));
      return next;
    });
  };

  const validateSinglePipeSave = (
    setupState: SetupState1,
    pipeIndex: number,
    singlePipePayload: ProjectSetupData
  ): { isValid: boolean; errors: string[] } => {
    const validation = validateSetupData(singlePipePayload);
    if (!validation.isValid) return validation;

    const shouldValidateJobNumbers =
      setupState.projectType === 'pipeline' ||
      (setupState.projectType === 'fabrication' && setupState.projectSubType !== 'spools');

    if (shouldValidateJobNumbers) {
      const crossPipeErrors = getCrossPipeDuplicateJobNumberErrors(setupState.pipeSizes).filter(
        (error) => error.startsWith(`Pipe ${pipeIndex + 1}:`)
      );
      if (crossPipeErrors.length > 0) {
        return { isValid: false, errors: crossPipeErrors };
      }
    }

    return { isValid: true, errors: [] };
  };

  const handleSaveSinglePipe = async (
    setupState: SetupState1,
    setSetupState: React.Dispatch<React.SetStateAction<SetupState1>>,
    pipeIndex: number
  ): Promise<boolean> => {
    const key = String(setupState.projectTypeId);
    const fullData = getCurrentSetupData();
    const section = fullData[key];
    if (!section?.pipeSizes?.[pipeIndex]) {
      customNotification.error('Save failed', 'Pipe data not found.');
      return false;
    }

    const singlePipePayload: ProjectSetupData = {
      [key]: {
        ...section,
        numberOfPipeSizes: setupState.pipeSizes.length,
        pipeSizes: [section.pipeSizes[pipeIndex]],
      },
    };

    const validation = validateSinglePipeSave(setupState, pipeIndex, singlePipePayload);
    if (!validation.isValid) {
      customNotification.warning(
        'Validation failed',
        validation.errors.join('\n') || 'Please fill the required fields for this pipe.'
      );
      return false;
    }

    try {
      const res = await SaveProjectSetup(singlePipePayload, true);
      if (!res?.data) {
        return false;
      }

      const savedPipes = res.data.pipes;
      if (Array.isArray(savedPipes) && savedPipes.length > 0) {
        const currentPipe = setupState.pipeSizes[pipeIndex];
        const apiPipe =
          savedPipes.find(
            (p: any) =>
              (currentPipe.pipeId && p.pipeId === currentPipe.pipeId) ||
              p.pipeOrder === pipeIndex + 1 ||
              p.pipeName === currentPipe.pipeName
          ) ?? savedPipes[savedPipes.length - 1];

        if (apiPipe?.pipeId) {
          setSetupState((prev) => ({
            ...prev,
            pipeSizes: prev.pipeSizes.map((p, i) =>
              i === pipeIndex ? { ...p, pipeId: apiPipe.pipeId } : p
            ),
          }));
        }

        const allOriginalPipes: any[] = [];
        Object.values(originalSetupData).forEach((setup: any) => {
          if (setup?.pipeSizes) {
            allOriginalPipes.push(...setup.pipeSizes);
          }
        });

        const originalPipe = allOriginalPipes.find(
          (op: any) =>
            (currentPipe.pipeId && op.pipeId === currentPipe.pipeId) ||
            (apiPipe?.pipeId && op.pipeId === apiPipe.pipeId) ||
            op.pipeOrder === pipeIndex + 1 ||
            op.pipeName === currentPipe.pipeName
        );
        const previousGroupId = originalPipe?.groupId ?? null;
        const savedPipePayload = section.pipeSizes[pipeIndex];

        if (isPipefitterEnabledForProject(project.project.projectTypes) && apiPipe) {
          try {
            await syncPipefitterProjectFromSetupPipe(project.project, apiPipe);
          } catch (pipefitterError: any) {
            const message =
              pipefitterError?.message ??
              pipefitterError?.response?.data?.message ??
              'Pipefitter project sync failed.';
            customNotification.warning('Pipefitter project', message);
          }
          if (apiPipe.pipeId) {
            try {
              await syncPipefitterPipeGroupFromSetupPipe(
                project.project,
                {
                  ...apiPipe,
                  groupId:
                    savedPipePayload?.groupId ??
                    currentPipe?.groupId ??
                    apiPipe.groupId,
                },
                previousGroupId
              );
            } catch (pipefitterError: any) {
              const message =
                pipefitterError?.message ??
                pipefitterError?.response?.data?.message ??
                'Pipefitter pipe group sync failed.';
              customNotification.warning('Pipefitter pipe group', message);
            }
          }
        }

        const hasJobNumberChanged = (() => {
          if (!originalPipe) return true;
          const currentJobNumbers = savedPipePayload?.jobNumber?.jobNumbers || [];
          const originalJobNumbers = originalPipe?.jobNumber?.jobNumbers || [];
          if (currentJobNumbers.length !== originalJobNumbers.length) return true;
          const normalizedCurrent = currentJobNumbers.map((j: any) => String(j || '').trim()).sort();
          const normalizedOriginal = originalJobNumbers.map((j: any) => String(j || '').trim()).sort();
          for (let i = 0; i < normalizedCurrent.length; i++) {
            if (normalizedCurrent[i] !== normalizedOriginal[i]) return true;
          }
          return false;
        })();

        if (apiPipe?.pipeId && hasJobNumberChanged) {
          dispatch(
            assignJobNumberToPipe({
              job_number: apiPipe.jobNumbers || savedPipePayload?.jobNumber?.jobNumbers || [],
              pipe_details_id: apiPipe.pipeId,
              project_id: project.project.projectId,
            })
          );
        }
      }

      updateOriginalPipeBaseline(setupState.projectTypeId, pipeIndex);

      customNotification.success(
        'Pipe saved',
        `Pipe ${pipeIndex + 1} saved successfully.`
      );

      return true;
    } catch {
      customNotification.error('Save failed', 'Failed to save pipe. Please try again.');
      return false;
    }
  };

  // Add save setup function with validation
  const handleSaveSetup = async (bool: boolean = false) => {
    try {
      setIsLoading(true);

      // Show validation when save is clicked
      setShowValidation(true);

      // Get current setup data
      const currentSetupData = getCurrentSetupData();
      // // Merge current data with existing setup data, preserving existing data for other tabs
      // const updatedSetupData: ProjectSetupData = {
      //   ...setupData,
      //   ...currentSetupData
      // };


      // Validate the setup data
      const validation = validateSetupData(currentSetupData);


      if (!validation.isValid) {
        console.log('[Setup Save] Validation failed – required fields / errors:', validation.errors);
        const message = validation.errors.length
          ? validation.errors.join('\n')
          : 'Please fill the required fields';
        customNotification.warning('Validation failed', message);
        return;
      }
      // setSetupData(updatedSetupData);
      const res = await SaveProjectSetup(currentSetupData, false);

      // After successful save (add/update), refresh pipe details from API
      if (res && res.data) {
        dispatch(getAllPipeDetails({ projectId: project.project.projectId }));
      }

      if (res && res.data && res.data.pipes) {
        const getAllOriginalPipesBeforeSave = (): any[] => {
          const allPipes: any[] = [];
          Object.values(originalSetupData).forEach((setup: any) => {
            if (setup?.pipeSizes) {
              allPipes.push(...setup.pipeSizes);
            }
          });
          return allPipes;
        };
        const originalPipesBeforeSave = getAllOriginalPipesBeforeSave();

        if (isPipefitterEnabledForProject(project.project.projectTypes)) {
          const lastPipe = getLastSavedPipe(res.data.pipes);
          if (lastPipe) {
            try {
              await syncPipefitterProjectFromSetupPipe(project.project, lastPipe);
            } catch (pipefitterError: any) {
              const message =
                pipefitterError?.message ??
                pipefitterError?.response?.data?.message ??
                'Pipefitter project sync failed.';
              customNotification.warning('Pipefitter project', message);
            }
          }

          for (const savedPipe of res.data.pipes) {
            if (!savedPipe?.pipeId) continue;
            const originalPipe = originalPipesBeforeSave.find(
              (op: any) => op.pipeId === savedPipe.pipeId
            );
            const currentFormPipe = Object.values(currentSetupData)
              .flatMap((setup: any) => setup?.pipeSizes ?? [])
              .find((p: any) => p.pipeId === savedPipe.pipeId);
            try {
              await syncPipefitterPipeGroupFromSetupPipe(
                project.project,
                {
                  ...savedPipe,
                  groupId:
                    currentFormPipe?.groupId ??
                    savedPipe.groupId,
                },
                originalPipe?.groupId ?? null
              );
            } catch (pipefitterError: any) {
              const message =
                pipefitterError?.message ??
                pipefitterError?.response?.data?.message ??
                'Pipefitter pipe group sync failed.';
              customNotification.warning('Pipefitter pipe group', message);
            }
          }
        }


        // Helper function to compare job numbers
        const hasJobNumberChanged = (currentPipe: any, originalPipe: any | undefined): boolean => {
          // If pipe is new (no original pipe), it's a change
          if (!originalPipe) {
            return true;
          }

          // Compare job numbers
          const currentJobNumbers = currentPipe?.jobNumber?.jobNumbers || [];
          const originalJobNumbers = originalPipe?.jobNumber?.jobNumbers || [];

          // Check if arrays have different lengths
          if (currentJobNumbers.length !== originalJobNumbers.length) {
            return true;
          }

          // Check if any job number value has changed
          const normalizedCurrent = currentJobNumbers.map((j: any) => String(j || '').trim()).sort();
          const normalizedOriginal = originalJobNumbers.map((j: any) => String(j || '').trim()).sort();

          if (normalizedCurrent.length !== normalizedOriginal.length) {
            return true;
          }

          for (let i = 0; i < normalizedCurrent.length; i++) {
            if (normalizedCurrent[i] !== normalizedOriginal[i]) {
              return true;
            }
          }

          return false;
        };

        // Get all pipes from original setup data across all project types
        const getAllOriginalPipes = (): any[] => {
          const allPipes: any[] = [];
          Object.values(originalSetupData).forEach((setup: any) => {
            if (setup?.pipeSizes) {
              allPipes.push(...setup.pipeSizes);
            }
          });
          return allPipes;
        };

        const allOriginalPipes = getAllOriginalPipes();

        // Only dispatch for pipes that have changed job numbers or are new
        res.data.pipes.forEach((pipe: any) => {
          const originalPipe = allOriginalPipes.find((op: any) =>
            op.pipeId === pipe.pipeId

          );

          // Get current pipe data from currentSetupData to compare job numbers
          let currentPipe: any = pipe;

          // console.log('currentPipe', currentPipe);
          // console.log('originalPipe', originalPipe);
          // Check if job number has changed
          if (currentPipe && hasJobNumberChanged(currentPipe, originalPipe)) {
            dispatch(assignJobNumberToPipe({
              job_number: pipe.jobNumbers || currentPipe?.jobNumber?.jobNumbers || [],
              pipe_details_id: pipe.pipeId,
              project_id: project.project.projectId
            }));
          }
        });
      }

      // Update local state with the saved data
      // if(res){
      // // setSetupData(updatedSetupData);
      setEditMode(false);
      setHasUnsavedChanges(false);
      OnChanged(false);
      baselineSyncNeededRef.current = true;
      dispatch(fetchProjectList());
      // Hide validation after successful save
      setShowValidation(false);

    } catch (error) {
      // Show error notification
      customNotification.error(
        'Save Failed',
        'Failed to save setup. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };
  // Function to filter setup data based on selected tab
  const getFilteredSetupData = () => {
    if (!setupData || Object.keys(setupData).length === 0) {
      return {};
    }

    const filteredData: any = {};

    if (setupTab === 'pipeline') {
      // Filter for pipeline data
      Object.entries(setupData).forEach(([key, data]: [string, any]) => {
        if (data && data.projectType === 'pipeline') {
          filteredData[key] = data;
        }
      });
    } else if (setupTab === 'fabrication') {
      // Filter for fabrication data
      Object.entries(setupData).forEach(([key, data]: [string, any]) => {
        if (data && data.projectType === 'fabrication') {
          filteredData[key] = data;
        }
      });
    } else if (setupTab === 'others') {
      // Filter for others data
      Object.entries(setupData).forEach(([key, data]: [string, any]) => {
        if (data && data.projectType === 'others') {
          filteredData[key] = data;
        }
      });
    }
    return filteredData;
  };



  // Separate state for each tab
  const [statePipeline, setStatePipeline] = useState<SetupState1>({
    numberOfPipeSizes: 0,
    projectId: project.project.projectId ? parseInt(project.project.projectId) : 0,
    projectTypeId: project.project.projectTypes.pipeline?.project_type_id || 0,
    projectType: "pipeline",
    projectSubType: "none",
    pipeSizes: []
  });



  // Separate state for each fabrication subtype
  const [stateBuckleArrestor, setStateBuckleArrestor] = useState<SetupState1>({
    numberOfPipeSizes: 0,
    projectId: project.project.projectId ? parseInt(project.project.projectId) : 0,
    projectTypeId: project.project.projectTypes.fabrication?.options?.buckleArrestor?.project_type_id || 0,
    projectType: "fabrication",
    projectSubType: "buckleArrestor",
    pipeSizes: []
  });

  const [statePipeInPipe, setStatePipeInPipe] = useState<SetupState1>({
    numberOfPipeSizes: 0,
    projectId: project.project.projectId ? parseInt(project.project.projectId) : 0,
    projectTypeId: project.project.projectTypes.fabrication?.options?.pipeInPipe?.project_type_id || 0,
    projectType: "fabrication",
    projectSubType: "pipeInPipe",
    pipeSizes: []
  });

  const [stateBulkhead, setStateBulkhead] = useState<SetupState1>({
    numberOfPipeSizes: 0,
    projectId: project.project.projectId ? parseInt(project.project.projectId) : 0,
    projectTypeId: project.project.projectTypes.fabrication?.options?.bulkhead?.project_type_id || 0,
    projectType: "fabrication",
    projectSubType: "bulkhead",
    pipeSizes: []
  });

  const [stateSpools, setStateSpools] = useState<SetupState1>({
    numberOfPipeSizes: 0,
    projectId: project.project.projectId ? parseInt(project.project.projectId) : 0,
    projectTypeId: project.project.projectTypes.fabrication?.options?.spools?.project_type_id || 0,
    projectType: "fabrication",
    projectSubType: "spools",
    pipeSizes: []
  });

  const [stateOthers, setStateOthers] = useState<SetupState1>({
    numberOfPipeSizes: 0,
    projectId: project.project.projectId ? parseInt(project.project.projectId) : 0,
    projectTypeId: project.project.projectTypes.others?.project_type_id || 0,
    projectType: "others",
    projectSubType: "none",
    pipeSizes: []
  });



  // Update form state when setupData changes - handle each tab separately
  useEffect(() => {

    if (!setupData || Object.keys(setupData).length === 0) {
      return;
    }


    // Handle pipeline data
    if (project.project.projectTypes.pipeline?.enabled) {
      const pipelineData = Object.values(setupData).find((data: any) =>
        data && data.projectType === 'pipeline' && data.pipeSizes && data.pipeSizes.length > 0
      ) as any;

      if (pipelineData && pipelineData.pipeSizes) {
        setStatePipeline({
          numberOfPipeSizes: pipelineData.pipeSizes.length,
          projectId: pipelineData.projectId || 0,
          projectTypeId: pipelineData.projectTypeId || 0,
          projectType: 'pipeline',
          projectSubType: pipelineData.projectSubType || 'none',
          pipeSizes: pipelineData.pipeSizes.map((pipe: any, index: number) => {
            // Convert API values (inches) to user's saved display units
            const pipeWithUnit = { ...pipe, unit: pipe.units || { pipeSize: defaultUnit, wallThickness: defaultUnit, length: defaultUnit, outputAssemblyLength: 'ft' } };
            const convertedPipe = mergeOorToleranceFromApi(
              convertPipefitterFieldsFromMm(convertSetupPipeFromInchesForDisplay(pipeWithUnit))
            );
            return {
              ...convertedPipe,
              productionData: pipe.productionData || { numberOfProduction: 0, isProductionAvailable: false },
              materialsData: pipe.materialsData || { numberOfMaterials: 0, isMaterialsAvailable: false },
              documentsData: pipe.documentsData || { numberOfDocs: 0, isDocumentsAvailable: false, documents: [] }
            };
          })
        });
      }
    }



    // Handle fabrication data for each subtype
    if (project.project.projectTypes.fabrication?.enabled) {
      // Handle buckleArrestor data
      const buckleArrestorData = Object.values(setupData).find((data: any) =>
        data && data.projectType === 'fabrication' && data.projectSubType === 'buckleArrestor' && data.pipeSizes && data.pipeSizes.length > 0
      ) as any;

      if (buckleArrestorData && buckleArrestorData.pipeSizes) {
        setStateBuckleArrestor({
          numberOfPipeSizes: buckleArrestorData.pipeSizes.length,
          projectId: buckleArrestorData.projectId || 0,
          projectTypeId: buckleArrestorData.projectTypeId || 0,
          projectType: 'fabrication',
          projectSubType: 'buckleArrestor',
          pipeSizes: buckleArrestorData.pipeSizes.map((pipe: any) => {
            const pipeWithUnit = { ...pipe, unit: pipe.units || { pipeSize: defaultUnit, wallThickness: defaultUnit, length: defaultUnit, outputAssemblyLength: 'ft' } };
            return mergeOorToleranceFromApi(
              convertPipefitterFieldsFromMm(convertSetupPipeFromInchesForDisplay(pipeWithUnit))
            );
          })
        });
      }
      else {
        setStateBuckleArrestor({
          numberOfPipeSizes: 0,
          projectId: project.project.projectId ? parseInt(project.project.projectId) : 0,
          projectTypeId: project.project.projectTypes.fabrication?.options?.buckleArrestor?.project_type_id || 0,
          projectType: "fabrication",
          projectSubType: "buckleArrestor",
          pipeSizes: []
        });
      }

      // Handle pipeInPipe data
      const pipeInPipeData = Object.values(setupData).find((data: any) =>
        data && data.projectType === 'fabrication' && data.projectSubType === 'pipeInPipe' && data.pipeSizes && data.pipeSizes.length > 0
      ) as any;

      if (pipeInPipeData && pipeInPipeData.pipeSizes) {
        setStatePipeInPipe({
          numberOfPipeSizes: pipeInPipeData.pipeSizes.length,
          projectId: pipeInPipeData.projectId || 0,
          projectTypeId: pipeInPipeData.projectTypeId || 0,
          projectType: 'fabrication',
          projectSubType: 'pipeInPipe',
          pipeSizes: pipeInPipeData.pipeSizes.map((pipe: any) => {
            const pipeWithUnit = { ...pipe, unit: pipe.units || { pipeSize: defaultUnit, wallThickness: defaultUnit, length: defaultUnit, outputAssemblyLength: 'ft' } };
            return mergeOorToleranceFromApi(
              convertPipefitterFieldsFromMm(convertSetupPipeFromInchesForDisplay(pipeWithUnit))
            );
          })
        });
      }

      // Handle bulkhead data
      const bulkheadData = Object.values(setupData).find((data: any) =>
        data && data.projectType === 'fabrication' && data.projectSubType === 'bulkhead' && data.pipeSizes && data.pipeSizes.length > 0
      ) as any;

      if (bulkheadData && bulkheadData.pipeSizes) {
        setStateBulkhead({
          numberOfPipeSizes: bulkheadData.pipeSizes.length,
          projectId: bulkheadData.projectId || 0,
          projectTypeId: bulkheadData.projectTypeId || 0,
          projectType: 'fabrication',
          projectSubType: 'bulkhead',
          pipeSizes: bulkheadData.pipeSizes.map((pipe: any) => {
            const pipeWithUnit = { ...pipe, unit: pipe.units || pipe.unit || { pipeSize: defaultUnit, wallThickness: defaultUnit, length: defaultUnit, outputAssemblyLength: 'ft' } };
            return mergeOorToleranceFromApi(
              convertPipefitterFieldsFromMm(convertSetupPipeFromInchesForDisplay(pipeWithUnit))
            );
          })
        });
      }

      // Handle spools data
      const spoolsData = Object.values(setupData).find((data: any) =>
        data && data.projectType === 'fabrication' && data.projectSubType === 'spools' && data.pipeSizes && data.pipeSizes.length > 0
      ) as any;

      if (spoolsData && spoolsData.pipeSizes) {
        setStateSpools({
          numberOfPipeSizes: spoolsData.pipeSizes.length,
          projectId: spoolsData.projectId || 0,
          projectTypeId: spoolsData.projectTypeId || 0,
          projectType: 'fabrication',
          projectSubType: 'spools',
          pipeSizes: spoolsData.pipeSizes.map((pipe: any) => {
            const pipeWithUnit = { ...pipe, unit: pipe.units || pipe.unit || { pipeSize: defaultUnit, wallThickness: defaultUnit, length: defaultUnit, outputAssemblyLength: 'ft' } };
            const displayed = convertSetupPipeFromInchesForDisplay(pipeWithUnit) as PipeSize1;
            const n = getNumberOfSpoolsFromPipe(displayed as PipeSize1 & Record<string, unknown>);
            return {
              ...displayed,
              spoolDetails: buildSpoolDetailsPayload(
                n,
                normalizeSpoolDetailsRows(n, getSpoolDetailsFromPipe(displayed))
              ),
            };
          })
        });
      } else {
        setStateSpools({
          numberOfPipeSizes: 0,
          projectId: project.project.projectId ? parseInt(project.project.projectId) : 0,
          projectTypeId: project.project.projectTypes.fabrication?.options?.spools?.project_type_id || 0,
          projectType: "fabrication",
          projectSubType: "spools",
          pipeSizes: []
        });
      }
    }

    // Handle others data 
    if (project.project.projectTypes.others?.enabled) {
      const othersData = Object.values(setupData).find((data: any) =>
        data && data.projectType === 'others' && data.pipeSizes && data.pipeSizes.length > 0
      ) as any;

      if (othersData && othersData.pipeSizes) {
        setStateOthers({
          numberOfPipeSizes: othersData.pipeSizes.length,
          projectId: othersData.projectId || 0,
          projectTypeId: othersData.projectTypeId || 0,
          projectType: 'others',
          projectSubType: othersData.projectSubType || 'none',
          pipeSizes: othersData.pipeSizes.map((pipe: any) => {
            const pipeWithUnit = { ...pipe, unit: pipe.units || { pipeSize: defaultUnit, wallThickness: defaultUnit, length: defaultUnit, outputAssemblyLength: 'ft' } };
            return mergeOorToleranceFromApi(
              convertPipefitterFieldsFromMm(convertSetupPipeFromInchesForDisplay(pipeWithUnit))
            );
          })
        });
      }
    }

    if (setupData && Object.keys(setupData).length > 0) {
      baselineSyncNeededRef.current = true;
    }
  }, [setupData, project.project.projectId, project.project.projectTypes.pipeline?.project_type_id, project.project.projectTypes.fabrication?.options?.buckleArrestor?.project_type_id, project.project.projectTypes.fabrication?.options?.pipeInPipe?.project_type_id, project.project.projectTypes.fabrication?.options?.bulkhead?.project_type_id, project.project.projectTypes.fabrication?.options?.spools?.project_type_id, project.project.projectTypes.others?.project_type_id]);

  // After API data loads into form state, sync comparison baseline so dirty check matches display units.
  useEffect(() => {
    if (!dataLoaded || isLoadingData || !baselineSyncNeededRef.current) {
      return;
    }
    const baseline = getCurrentSetupData();
    if (!baseline || Object.keys(baseline).length === 0) {
      return;
    }
    baselineSyncNeededRef.current = false;
    setOriginalSetupData(JSON.parse(JSON.stringify(baseline)));
    OnChanged(false);
    setHasUnsavedChanges(false);
  }, [
    statePipeline,
    stateBuckleArrestor,
    statePipeInPipe,
    stateBulkhead,
    stateSpools,
    stateOthers,
    dataLoaded,
    isLoadingData,
  ]);

  useEffect(() => {

    if (!dataLoaded || isLoadingData) {
      return;
    }
    if (baselineSyncNeededRef.current) {
      return;
    }
    const spoolsDetailDirty = hasSpoolsSetupDetailChanges();
    const hasChanges =
      hasAnyValidNumberOfPipeSizes() &&
      (spoolsDetailDirty || hasActualChanges());

    if (!hasChanges) {
      OnChanged(false);
      setHasUnsavedChanges(false);
    } else {
      OnChanged(true);
      setHasUnsavedChanges(true);
    }
  }, [
    statePipeline,
    stateBuckleArrestor,
    statePipeInPipe,
    stateBulkhead,
    stateSpools,
    stateOthers,
    originalSetupData,
    dataLoaded,
    isLoadingData,
  ]);

  // Add browser-level confirmation for unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges && hasActualChanges()) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return 'You have unsaved changes. Are you sure you want to leave?';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // useEffect(() => {
  //   // Only check for unsaved changes after initial data has been loaded
  //   if (!dataLoaded || isLoadingData) {
  //     return;
  //   }

  //  const hasChanges = hasActualChanges() && hasAnyValidPipesInAnyState();

  //   if (hasChanges) {
  //     setHasUnsavedChanges(true);
  //   }


  // }, [statePipeline.pipeSizes, stateBuckleArrestor.pipeSizes, statePipeInPipe.pipeSizes, stateBulkhead.pipeSizes, stateOthers.pipeSizes, dataLoaded, originalSetupData]);

  return (
    <>
      {isLoadingData ? (
        <div className="flex items-center justify-center min-h-[700px]">
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#EB1C37]"></div>
            <span className="text-gray-600 text-lg">Loading setup data...</span>
          </div>
        </div>
      ) : (
        <div>
          <Card className="border-none shadow-none">
            <CardContent className="px-6 pt-2 pb-6">
              {/* Setup Category Tabs */}
              <Tabs className="w-full"
                value={setupTab}
                onValueChange={(value) => {
                  setSetupTab(value as 'pipeline' | 'fabrication' | 'others');
                }}
              >
                <div className="sticky top-0 bg-white rounded-t-lg z-10 border-b-2 border-[#E2E5EA] flex items-end justify-between gap-4">
                  <TabsList className="h-auto justify-start gap-8 bg-transparent px-0 py-0"
                  >
                    {project.project.projectTypes.pipeline?.enabled && (
                      <TabsTrigger
                        className="relative inline-flex items-center border-b-2 border-transparent -mb-[2px] px-1 pb-3 pt-2 text-[13px] font-medium text-heritage-grey hover:text-blueprint-blue data-[state=active]:border-hero-red data-[state=active]:text-blueprint-blue transition-colors duration-150"
                        value="pipeline">
                        <Cable className="h-4 w-4 mr-2" />Pipeline
                      </TabsTrigger>
                    )}
                    {project.project.projectTypes.fabrication?.enabled && (
                      <TabsTrigger
                        className="relative inline-flex items-center border-b-2 border-transparent -mb-[2px] px-1 pb-3 pt-2 text-[13px] font-medium text-heritage-grey hover:text-blueprint-blue data-[state=active]:border-hero-red data-[state=active]:text-blueprint-blue transition-colors duration-150"
                        value="fabrication">
                        <Wrench className="h-4 w-4 mr-2" />Fabrication
                      </TabsTrigger>
                    )}
                    {project.project.projectTypes.others?.enabled && (
                      <TabsTrigger
                        className="relative inline-flex items-center border-b-2 border-transparent -mb-[2px] px-1 pb-3 pt-2 text-[13px] font-medium text-heritage-grey hover:text-blueprint-blue data-[state=active]:border-hero-red data-[state=active]:text-blueprint-blue transition-colors duration-150"
                        value="others">
                        <Activity className="h-4 w-4 mr-2" />Others
                      </TabsTrigger>
                    )}
                  </TabsList>
                  {canEdit && (
                    <Button
                      onClick={() => handleSaveSetup(true)}
                      disabled={isLoading || !hasUnsavedChanges}
                      size="sm"
                      className="bg-[#EB1C37] text-white hover:bg-[#EB1C37]/90 flex items-center gap-2 justify-center flex-shrink-0 self-center mr-2 mb-1"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4" />
                          Save
                        </>
                      )}
                    </Button>
                  )}
                </div>

                {project.project.projectTypes.pipeline?.enabled && (
                  <TabsContent value="pipeline">
                    <PipelineSetup
                      ref={pipelineSetupRef}
                      project={project}
                      manufacturingList={manufacturingList}
                      setManufacturingList={setManufacturingList}
                      unit={unit}
                      setUnit={setUnit}
                      showValidation={showValidation}
                      setShowValidation={setShowValidation}
                      setupData={getFilteredSetupData()}
                      state={statePipeline}
                      setState={setStatePipeline}
                      setHasUnsavedChanges={setHasUnsavedChanges}
                      OnSave={handleSaveSetup}
                      onSavePipe={(pipeIndex) =>
                        handleSaveSinglePipe(statePipeline, setStatePipeline, pipeIndex)
                      }
                      canEdit={canEdit}
                    />
                  </TabsContent>
                )}
                {project.project.projectTypes.fabrication?.enabled && (
                  <TabsContent value="fabrication">
                    <FabricationSetup
                      ref={fabricationSetupRef}
                      project={project}
                      manufacturingList={manufacturingList}
                      setManufacturingList={setManufacturingList}
                      unit={unit}
                      setUnit={setUnit}
                      showValidation={showValidation}
                      setupData={
                        (() => {

                          return setupData
                        })()
                      }
                      buckleArrestorState={stateBuckleArrestor}
                      setBuckleArrestorState={setStateBuckleArrestor}
                      pipeInPipeState={statePipeInPipe}
                      setPipeInPipeState={setStatePipeInPipe}
                      bulkheadState={stateBulkhead}
                      setBulkheadState={setStateBulkhead}
                      spoolsState={stateSpools}
                      setSpoolsState={setStateSpools}
                      setHasUnsavedChanges={setHasUnsavedChanges}
                      canEdit={canEdit}
                      onSaveBuckleArrestorPipe={(pipeIndex) =>
                        handleSaveSinglePipe(stateBuckleArrestor, setStateBuckleArrestor, pipeIndex)
                      }
                      onSavePipeInPipePipe={(pipeIndex) =>
                        handleSaveSinglePipe(statePipeInPipe, setStatePipeInPipe, pipeIndex)
                      }
                      onSaveBulkheadPipe={(pipeIndex) =>
                        handleSaveSinglePipe(stateBulkhead, setStateBulkhead, pipeIndex)
                      }
                      onSaveSpoolsPipe={(pipeIndex) =>
                        handleSaveSinglePipe(stateSpools, setStateSpools, pipeIndex)
                      }
                    />
                  </TabsContent>
                )}

                {project.project.projectTypes.others?.enabled && (
                  <TabsContent value="others">
                    <OthersSetup
                      ref={othersSetupRef}
                      project={project}
                      manufacturingList={manufacturingList}
                      setManufacturingList={setManufacturingList}
                      unit={unit}
                      setUnit={setUnit}
                      showValidation={showValidation}
                      setShowValidation={setShowValidation}
                      setupData={getFilteredSetupData()}
                      state={stateOthers}
                      setState={setStateOthers}
                      setHasUnsavedChanges={setHasUnsavedChanges}
                      onSavePipe={(pipeIndex) =>
                        handleSaveSinglePipe(stateOthers, setStateOthers, pipeIndex)
                      }
                      canEdit={canEdit}
                    />

                  </TabsContent>
                )}

              </Tabs>
            </CardContent>
          </Card>


        </div>
      )}
    </>
  );
}; 